/**
 * Descriptor-driven Cooling System Summary source (#997 — Story 4.6).
 *
 * Reproduces the bespoke `getCoolingSystemEditorSummaryDefinitions()` +
 * `getCoolingSystemNoteSummaryDefinitions()` row sets and per-unit value /
 * title projection from the Cooling System descriptor + the shared Summary
 * factory, byte-identically against the Story 4.6 golden masters
 * (`cooling-system-summary-rows.json`, `cooling-system-summary-filled.json`).
 * This is the engine-driven replacement Story 4.6 re-points the Summary and
 * the golden tests at — same oracle, new implementation.
 *
 * Row-set layout mirrors what the bespoke code produced from
 * `requestFieldRegistry.ts`:
 *
 *   - Per-unit editor group (`subsectionId: 'coolingInformation'` — matches
 *     `coolingSystemEditorSummaryRows` at `requestFieldRegistry.ts:300`):
 *     `name`, `status`, then the 9 requirements fields
 *     (`coolingIsExistingSystemOnsite`, `coolingIsChilledWaterAvailable`,
 *     `coolingTotalAvailableCapacityKw`, `coolingDescription`,
 *     `coolingRequiredRedundancyForSystem`,
 *     `coolingTempRisePrefThroughItEquipment`, `coolingDescribeApproach`,
 *     `coolingRearDoorHexDesired`, `coolingDescribeRearDoorLoop`) — the
 *     `subsection` marker stamps on the first row (via
 *     `buildEditorSubsectionSummaryRows`).
 *   - Per-unit note group (`subsectionId: 'additionalInformation'`):
 *     `coolingAdditionalReadinessInfo` — marker stamps here. NO
 *     `labelOverrides` needed: the COOLING_READINESS_FIELDS registry entry
 *     for `coolingAdditionalReadinessInfo` already carries the label
 *     "Additional Cooling Readiness Information"
 *     (`cooling-readiness.fields.ts:84`), and the current bespoke
 *     `coolingSystemNoteSummaryRows` applies no override (contrast SU / LU /
 *     PD which override "Notes" to a section-specific label).
 *
 * Cooling has NO request-level shared field (per registry
 * `COOLING_EDITOR_SHARED_FIELD_KEYS = []` — #997 Bug 9 moved both former
 * shared fields per-instance), so `sharedFieldKeys` is empty on the descriptor
 * and there is no shared-row accessor here.
 *
 * The identity fields `name`/`status` are not registry fields, so their labels
 * fall back to the editor-slice `shortLabel` values ("Name" / "Type") supplied
 * by `COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS`. No `labelOverrides` are needed
 * for them (the fallback is already correct).
 */

import { COOLING_SYSTEM_DESCRIPTOR } from './coolingSystem.descriptor';
import { repeatableInstanceNameTypeRowValue } from '../../summary/repeatableInstanceSummaryDisplay';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { coolingReadinessSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  COOLING_NOTES_SUBSECTION_FIELDS,
  COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { COOLING_READINESS_FIELDS } from '../fields/steps/cooling-readiness.fields';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { CoolingSystemConfiguration } from '../../types/coolingSystem';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR = COOLING_SYSTEM_DESCRIPTOR.permissionRef.rbacAnchorDataField;
const NOTE_RBAC_ANCHOR = 'coolingAdditionalReadinessInfo';

/**
 * Registry field metadata (labels) the factory resolves per field. Keeping
 * this pointed at the COOLING_READINESS_FIELDS registry (rather than the
 * descriptor's `fields`) mirrors the bespoke source and keeps labels /
 * subsection metadata flowing through the same lookup the engine expects.
 */
const REGISTRY_FIELDS = COOLING_READINESS_FIELDS;

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  rbacAnchorDataField: string,
  labelOverrides?: Readonly<Record<string, string>>,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField,
    subsectionId,
    labelOverrides,
  });
}

/**
 * Full ordered per-unit Cooling System Summary row set (editor + note),
 * byte-identical to the bespoke `getPerCoolingSystemSummaryDefinitions()`
 * output (which concatenates `getCoolingSystemEditorSummaryDefinitions()` +
 * `getCoolingSystemNoteSummaryDefinitions()`).
 */
export function getCoolingSystemDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(
      COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
      'coolingInformation',
      RBAC_ANCHOR,
    ),
    ...buildGroup(
      COOLING_NOTES_SUBSECTION_FIELDS,
      'additionalInformation',
      NOTE_RBAC_ANCHOR,
    ),
  ];
}

/**
 * Per-unit Cooling System Summary value — delegates to
 * `repeatableInstanceNameTypeRowValue` for the identity fields (`name`,
 * Existing/New `status`) and `coolingReadinessSummaryRowValue` for the
 * Cooling detail fields (handles the Yes/No projection for the boolean-backed
 * `coolingRearDoorHexDesired`; string-backed yesNo fields pass through
 * unchanged). Re-homed here so the Summary value path no longer routes
 * through the bespoke display module.
 */
export function coolingSystemDescriptorUnitRowValue(
  unit: CoolingSystemConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  const nameTypeValue = repeatableInstanceNameTypeRowValue(unit, dataField);
  if (nameTypeValue !== undefined) {
    return nameTypeValue;
  }
  return coolingReadinessSummaryRowValue(
    unit as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a Cooling System unit's display name (user-set `name` →
 * `Cooling System N` fallback based on `displayOrder`, else `null` so the
 * caller falls back to the ordinal). Kept here so the Summary title no
 * longer depends on the bespoke display module.
 */
function resolveCoolingSystemSummaryDisplayName(
  unit: CoolingSystemConfiguration,
): string | null {
  const name = unit.name?.trim();
  if (name) {
    return name;
  }
  if (unit.displayOrder != null && unit.displayOrder > 0) {
    return `Cooling System ${unit.displayOrder}`;
  }
  return null;
}

/**
 * Per-unit Summary card title — `Existing/New — <name>` (fallback
 * `Existing/New Cooling System N`).
 */
export function getCoolingSystemDescriptorUnitTitle(
  unit: CoolingSystemConfiguration,
  index: number,
): string {
  const status = unit.status === 'existing' ? 'Existing' : 'New';
  const name = resolveCoolingSystemSummaryDisplayName(unit);
  return name ? `${status} — ${name}` : `${status} Cooling System ${index + 1}`;
}
